import React from "react";
import { Loader2, ArrowLeft, ArrowRight, type LucideIcon } from "lucide-react";
import { Button, Label } from "@/components/ds";
import { cn } from "@/lib/utils";

export function Field({
  label, required, hint, children, testid,
}: {
  label: string; required?: boolean; hint?: string; children: React.ReactNode; testid?: string;
}) {
  return (
    <div data-testid={testid}>
      <Label>
        {label}
        {required && <span className="text-red-500"> *</span>}
      </Label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export function StepShell({
  icon: Icon, title, description, children, onBack, onContinue, continueLabel = "Save & continue",
  saving, continueDisabled, secondary,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  children: React.ReactNode;
  onBack?: () => void;
  onContinue: () => void;
  continueLabel?: string;
  saving?: boolean;
  continueDisabled?: boolean;
  secondary?: React.ReactNode;
}) {
  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="mb-6 flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
      </div>

      <div className="space-y-5">{children}</div>

      <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
        <div>
          {onBack && (
            <Button variant="ghost" onClick={onBack} disabled={saving} data-testid="onboarding-back">
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {secondary}
          <Button
            onClick={onContinue}
            disabled={saving || continueDisabled}
            data-testid="onboarding-continue"
            className={cn("min-w-[168px]")}
          >
            {saving
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
              : <>{continueLabel} <ArrowRight className="h-4 w-4" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
